import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import { IFamilyPlanInvitation } from 'fitbeat.models';
import { IAppStore } from '../HomeWorkouts';
import { LOGOUT } from '../utils/LogoutHelper';
import { clearDeepLinkParams } from '../utils/notifications/actions';
import { UserProfile, UserProfileService } from 'fitbeat.gym-core';
import * as types from './actionTypes';
import { IPendingInvite, ISwitchMemberUserProfile } from './reducer';
import { NavigatorService } from '../utils/NavigatorService';
import { Logger } from '../utils';

export const loadSwitchMemberScreen = (userProfile: UserProfile, userServiceClient: UserServiceClient) => {
  return async (dispatch) => {
    await loadFamilyMembers(dispatch, userServiceClient, userProfile.uid);
  };
};

export const loadSelectMemberScreen = (currentLoggedInUserId: string, userServiceClient: UserServiceClient) => {
  return async (dispatch) => {
    await loadFamilyMembers(dispatch, userServiceClient, currentLoggedInUserId);
  };
};

export const swopUser = (
  swoppingToUser: UserProfile,
  currentLoggedInUserUid: string,
  userServiceClient: UserServiceClient,
  userProfileService: any,
  navigation: any,
  setUpInitialUserData: any,
  swoppingToUserPin?: string,
) => {
  return async (dispatch) => {
    if (
      swoppingToUser.familyPlanSettings &&
      swoppingToUser.familyPlanSettings.hasPin &&
      navigation.state.routeName === 'SwitchMember'
      ) {
      navigation.navigate('EnterProfilePin', {swoppingToUser});
      return;
    }
    dispatch(swopUserRequest(swoppingToUser.uid));

    try {
      Logger.leaveBreadcrumb('swop user', {
        currentLoggedInUserUid,
        swoppingToUserUid: swoppingToUser.uid,
      });

      const { token } = await userServiceClient.userTokenSwop(swoppingToUser.uid, swoppingToUserPin);
      await auth().signInWithCustomToken(token);
      await userProfileService.refreshCurrentUserProfile();
      NavigatorService.navigateContainerOne('AuthLoading');
      dispatch({ type: LOGOUT });
      await setUpInitialUserData();
      dispatch(swopUserSuccess());
    } catch (error) {
      dispatch(swopUserFailure(error, currentLoggedInUserUid));
    }
  };
};

export const selectMember = (
  swoppingToUser: UserProfile,
  currentLoggedInUserUid: string,
  userServiceClient: UserServiceClient,
  navigation: any,
  swoppingToUserPin?: string,
) => {
  // todo: try and combine with swopUser
  return async (dispatch) => {
    if (
      swoppingToUser.familyPlanSettings &&
      swoppingToUser.familyPlanSettings.hasPin &&
      navigation.state.routeName === 'SelectMember'
      ) {
      navigation.navigate('EnterProfilePin', {swoppingToUser, enterPinFromSelectMember: true});
      return;
    }
    dispatch(swopUserRequest(swoppingToUser.uid));

    try {
      Logger.leaveBreadcrumb('select member', {
        currentLoggedInUserUid,
        swoppingToUserUid: swoppingToUser.uid,
      });

      const { token } = await userServiceClient.userTokenSwop(swoppingToUser.uid, swoppingToUserPin);
      await auth().signInWithCustomToken(token);
      dispatch({ type: LOGOUT });
      NavigatorService.navigateContainerOne('AuthLoading');
      dispatch(swopUserSuccess());
    } catch (error) {
      dispatch(swopUserFailure(error, currentLoggedInUserUid));
    }
  };
}

export const loadInviteMemberScreen = (navigation: any) => {
  return async (dispatch) => {
    navigation.navigate('InviteMember');
    dispatch(loadInviteMemberScreenRequest());
  };
};

export const loadMemberSettingsScreen = (navigation: any, userProfile: ISwitchMemberUserProfile) => {
  return () => {
    navigation.navigate('MemberSettings', {userProfile});
  };
};

export const loadChooseProfileColorScreen = (navigation: any, profileColor: string) => {
  return () => {
    navigation.navigate('ChooseProfileColor', {profileColor});
  };
};

export const loadSetProfilePinScreen = (navigation: any) => {
  return () => {
    navigation.navigate('SetProfilePin');
  };
};

export const loadSignInScreen = (navigation: any) => {
  return () => {
    navigation.navigate('Login');
  };
};

export const loadInvitationCodeWelcomeScreen = (navigation: any) => {
  return () => {
    navigation.navigate('InvitationCodeWelcome');
  };
};

export const loadConfirmCancelScreen = (navigation: any, userProfile: ISwitchMemberUserProfile, memberIsBeingRemovedByAdmin: boolean = false) => {
  return (dispatch) => {
    const userToRemove = userProfile;
    dispatch(loadConfirmRemoveScreenRequest());
    navigation.navigate('ConfirmRemove', {userToRemove, memberIsBeingRemovedByAdmin});
  };
};

export const closeInvitationCodeWelcomeScreen = (navigation: any, setUpInitialUserData: any) => {
  return async (dispatch) => {
    await setUpInitialUserData();
    navigation.navigate('HomeLoading');
    dispatch(clearDeepLinkParams(['invitationCode']));
    dispatch(resetInvitationCodeWelcomeScreen());
  };
};

export const inviteFamilyMember = (
  navigation: any,
  userServiceClient: UserServiceClient,
  userEmailToInvite: string,
  userProfile: UserProfile,
) => {
  return async (dispatch) => {
    dispatch(inviteFamilyMemberRequest());
    try {
      await userServiceClient.inviteUserToFamilyPlan(userEmailToInvite);
      await loadFamilyMembers(dispatch, userServiceClient, userProfile.uid);
      navigation.navigate('SwitchMember');
      dispatch(inviteFamilyMemberSuccess());
    } catch (error) {
      dispatch(inviteFamilyMemberFailure(error));
    }
  };
};

export const acceptFamilyPlanInvitation = (userServiceClient: UserServiceClient) => {
  return async (dispatch, getState: () => IAppStore) => {
    const { deepLinkParamaters: { invitationCode } } = getState();
    dispatch(acceptFamilyPlanInvitationRequest());
    try {
      const acceptFamilyPlanInvitationResponse = await userServiceClient.acceptFamilyPlanInvitation(invitationCode);
      dispatch(acceptFamilyPlanInvitationSuccess(acceptFamilyPlanInvitationResponse));
    } catch (error: any) {
      dispatch(acceptFamilyPlanInvitationFailure(error.message));
    }
  };
};

export const cancelPendingInvitation = (userServiceClient: UserServiceClient, pendingInvite: IPendingInvite) => {
  return async (dispatch) => {
    dispatch(cancelPendingFamilyPlanInvitationRequest(pendingInvite));
    try {
      await userServiceClient.cancelPendingFamilyPlanInvitation(pendingInvite.code);
      dispatch(cancelPendingFamilyPlanInvitationSuccess(pendingInvite.code));
    } catch (error: any) {
      dispatch(cancelPendingFamilyPlanInvitationFailure(error, pendingInvite));
    }
  };
}

export const removeFamilyPlanMember = (
  userServiceClient: UserServiceClient,
  userProfileService: UserProfileService,
  setUpInitialUserData: any,
  navigation: any,
  userIdToRemove: string,
  memberIsBeingRemovedByAdmin: boolean,
) => {
  return async (dispatch) => {
    dispatch(removeFamilyPlanMemberRequest());
    if (memberIsBeingRemovedByAdmin) {
      try {
        await userServiceClient.removeFamilyMember(userIdToRemove);
        dispatch(removeFamilyPlanMemberSuccess(userIdToRemove));
        navigation.navigate('SwitchMember');
      } catch (error: any) {
        dispatch(removeFamilyPlanMemberFailure(error));
      }
    } else {
      try {
        await userServiceClient.removeFamilyMember(userIdToRemove);
        await userProfileService.refreshCurrentUserProfile();
        await setUpInitialUserData();
        navigation.navigate('HomeLoading');
        dispatch(removeFamilyPlanMemberSuccess(userIdToRemove));
      } catch (error: any) {
        dispatch(removeFamilyPlanMemberFailure(error));
      }
    }
  };
}

export const loadInvitationCodeSignupScreen = () => {
  return {
    type: types.LOAD_INVITATION_CODE_SIGNUP_SCREEN,
    payload: {},
  };
};

export const manageMembers = () => {
  return {
    type: types.MANAGE_MEMBERS,
  };
}

export const manageMembersDone = () => {
  return {
    type: types.MANAGE_MEMBERS_DONE,
  }
}

const switchMemberScreenLoadingRequest = () => {
  return {
    type: types.SWITCH_MEMBER_SCREEN_LOADING_REQUEST,
    payload: {},
  };
};

const switchMemberScreenLoadingSuccess = (currentLoggedInUserId, familyMembers, invitedUsers, canInviteMoreFamilyMembers) => {
  return {
    type: types.SWITCH_MEMBER_SCREEN_LOADING_SUCCESS,
    payload: {
        invitedUsers,
        familyMembers,
        currentLoggedInUserId,
        canInviteMoreFamilyMembers,
    },
  };
};

const switchMemberScreenLoadingFailure = (error) => {
  return {
    type: types.SWITCH_MEMBER_SCREEN_LOADING_FAILURE,
    payload: {
      error,
    },
  };
};

const swopUserRequest = (uid) => {
  return {
    type: types.SWOP_USER_REQUEST,
    payload: {
      swoppingToUid: uid,
    },
  };
};

const swopUserSuccess = () => {
  return {
    type: types.SWOP_USER_SUCCESS,
    payload: {},
  };
};

export const pinAttemptChanged = (pin: string) => {
    return {
      type: types.PIN_ATTEMPT_CHANGED,
      payload: { pin },
    };
};

const swopUserFailure = (error, currentLoggedInUid: string) => {
  return {
    type: types.SWOP_USER_FAILURE,
    payload: {
      error,
      currentLoggedInUid,
    },
  };
};

const loadInviteMemberScreenRequest = () => {
  return {
    type: types.LOAD_INVITE_MEMBER_SCREEN,
    payload: {},
  };
}

const loadConfirmRemoveScreenRequest = () => {
  return {
    type: types.LOAD_CONFIRM_REMOVE_SCREEN,
  };
}

const inviteFamilyMemberRequest = () => {
  return {
    type: types.INVITE_FAMILY_MEMBER_REQUEST,
    payload: {},
  };
};

const inviteFamilyMemberSuccess = () => {
  return {
    type: types.INVITE_FAMILY_MEMBER_SUCCESS,
    payload: {},
  };
};

const inviteFamilyMemberFailure = (error) => {
  return {
    type: types.INVITE_FAMILY_MEMBER_FAILURE,
    payload: {
      error,
    },
  };
};

const acceptFamilyPlanInvitationRequest = () => {
  return {
    type: types.ACCEPT_FAMILY_PLAN_INVITATION_REQUEST,
    payload: {},
  };
};

const acceptFamilyPlanInvitationSuccess = (acceptFamilyPlanInvitationResponse: {message: string, success: boolean}) => {
  return {
    type: types.ACCEPT_FAMILY_PLAN_INVITATION_SUCCESS,
    payload: {
      joinedFamilyPlanInformation: acceptFamilyPlanInvitationResponse.message,
      successfullyJoinedFamilyPlan: acceptFamilyPlanInvitationResponse.success,
    },
  };
};

const acceptFamilyPlanInvitationFailure = (error) => {
  return {
    type: types.ACCEPT_FAMILY_PLAN_INVITATION_FAILURE,
    payload: {
      error,
    },
  };
};

const cancelPendingFamilyPlanInvitationRequest = (pendingInvite: IPendingInvite) => {
  return {
    type: types.CANCEL_PENDING_FAMILY_PLAN_INVITATION_REQUEST,
    payload: {
      pendingInvite,
    },
  };
};

const cancelPendingFamilyPlanInvitationSuccess = (invitationCode: string) => {
  return {
    type: types.CANCEL_PENDING_FAMILY_PLAN_INVITATION_SUCCESS,
    payload: {
      invitationCode,
    },
  };
};

const cancelPendingFamilyPlanInvitationFailure = (error, pendingInvite: IPendingInvite) => {
  return {
    type: types.CANCEL_PENDING_FAMILY_PLAN_INVITATION_FAILURE,
    payload: {
      error,
      pendingInvite,
    },
  };
};

const resetInvitationCodeWelcomeScreen = () => {
  return {
    type: types.CLOSE_INVITATION_CODE_WELCOME_SCREEN,
    payload: {},
  };
}

const removeFamilyPlanMemberRequest = () => {
  return {
    type: types.REMOVE_FAMILY_PLAN_MEMBER_REQUEST,
  };
};

const removeFamilyPlanMemberSuccess = (userIdToRemove: string) => {
  return {
    type: types.REMOVE_FAMILY_PLAN_MEMBER_SUCCESS,
    payload: {
      userIdToRemove,
    },
  };
};

const removeFamilyPlanMemberFailure = (error) => {
  return {
    type: types.REMOVE_FAMILY_PLAN_MEMBER_FAILURE,
    payload: {
      error,
    },
  };
};

const loadFamilyMembers = async (dispatch: any, userServiceClient: UserServiceClient, loggedInUserProfileId: string) => {
  dispatch(switchMemberScreenLoadingRequest());
  try {
    const {
      familyMembers,
      invited,
      canInviteMoreFamilyMembers,
    }: {
      familyMembers: UserProfile[];
      invited: IFamilyPlanInvitation[];
      canInviteMoreFamilyMembers: boolean;
    } = await userServiceClient.getAllUsersInFamilyPlan();
    dispatch(switchMemberScreenLoadingSuccess(loggedInUserProfileId, familyMembers, invited, canInviteMoreFamilyMembers));
  } catch (error) {
    dispatch(switchMemberScreenLoadingFailure(error));
  }
}

export const updateUserProfileColor = (
  userServiceClient: UserServiceClient, userProfileService: UserProfileService, profileColor: string, userId: string) => {
  return async (dispatch: any) => {
    dispatch({type: types.UPDATE_USER_PROFILE_COLOR_REQUEST});
    try {
      const user = await userServiceClient.updateUserProfileColor(profileColor);
      await userProfileService.refreshCurrentUserProfile();
      dispatch(
        {
          type: types.UPDATE_USER_PROFILE_COLOR_SUCCESS,
          payload: {
            userId,
            profileColor: user.familyPlanSettings.profileColor,
          }});
    } catch (error) {
      dispatch({type: types.UPDATE_USER_PROFILE_COLOR_FAILURE});
    }
  };
};

export const updateUserProfilePin = (
  userServiceClient: UserServiceClient, pin: string, userId: string) => {
  return async (dispatch: any) => {
    dispatch({type: types.UPDATE_USER_PROFILE_PIN_REQUEST});
    try {
      const user = await userServiceClient.updateUserProfilePin(pin);
      dispatch(
        {
          type: types.UPDATE_USER_PROFILE_PIN_SUCCESS,
          payload: {
            userId,
            pin: user.familyPlanSettings.pin,
          }});
    } catch (error) {
      dispatch({type: types.UPDATE_USER_PROFILE_PIN_FAILURE});
    }
  };
};
